import apiClient, { type ApiError } from './apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import { API_ENDPOINTS } from './config'

/**
 * Property data from API
 */
export interface Property {
  property_id: number
  property_name: string
  property_address?: string
  property_address2?: string
  property_city?: string
  property_state?: string
  property_zipcode?: string
  listed?: number | string
  pmc_invite?: string
  disable_uc?: string
  // Additional fields from application data
  lease_duration?: string // Lease Duration
  email?: string // Leasing Consultant/Property Manager Email
  mobile_no?: string // Leasing Consultant/Property Manager Phone
  // Legacy fields for backward compatibility
  address?: string
  city?: string
  state?: string
  zip_code?: string
  [key: string]: unknown
}

/**
 * Agent data from API
 */
export interface Agent {
  agent_id?: number
  agent_name?: string
  email?: string
  mobile_no?: string
  property_id?: number
  invite_code?: string
  [key: string]: unknown
}

/**
 * Get all listed properties
 * @param listed - Filter by listed status (1 for listed, 0 for unlisted)
 * @returns Array of properties
 * @throws ApiError if request fails
 */
export async function getAllProperties(listed: number = 1): Promise<Property[]> {
  try {
    const response = await apiClient.get<Property[]>(
      `${API_ENDPOINTS.ALL_PROPERTY}?listed=${listed}`
    )
    
    // Handle array response
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    // Handle wrapped response
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const wrappedData = (response.data as { data: Property[] }).data
      return Array.isArray(wrappedData) ? wrappedData : []
    }
    
    return []
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch properties.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Get agent information for a property
 * @param propertyId - Property ID
 * @param inviteCode - Invite code
 * @returns Agent data
 * @throws ApiError if request fails
 */
export async function getAgent(
  propertyId: number | string,
  inviteCode: string
): Promise<Agent> {
  try {
    const queryParams = new URLSearchParams({
      property_id: String(propertyId),
      invite_code: inviteCode,
    })
    
    const response = await apiClient.get<Agent>(
      `${API_ENDPOINTS.ALL_AGENT}?${queryParams.toString()}`
    )
    
    return response.data || {}
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch agent information.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Get single property by id — GET `/api/property/:id` (Angular property detail).
 */
export async function getPropertyById(
  id: string | number,
): Promise<Property> {
  try {
    const response = await apiClient.get<Property>(
      API_ENDPOINTS.PROPERTY_DETAILS(id),
    )
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch property details.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Get agents list for a property (Angular `getAgents`).
 * GET `/api/all_agent` with { property_id, invite_code }.
 */
export async function getAgentsForProperty(
  propertyId: number | string,
  inviteCode: string,
): Promise<Agent[]> {
  try {
    const queryParams = new URLSearchParams({
      property_id: String(propertyId),
      invite_code: String(inviteCode ?? ''),
    })

    const response = await apiClient.get<Agent[] | unknown>(
      `${API_ENDPOINTS.ALL_AGENT}?${queryParams.toString()}`,
    )

    const raw = (response as { data?: unknown })?.data
    if (Array.isArray(raw)) return raw as Agent[]
    if (raw && typeof raw === 'object' && 'data' in (raw as Record<string, unknown>)) {
      const wrapped = (raw as { data?: unknown }).data
      return Array.isArray(wrapped) ? (wrapped as Agent[]) : []
    }
    return []
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch agents.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

